<template>
  <div class="payment-block">
    <div
      v-if="showEyebrow"
      class="payment-block__eyebrow"
    >
      {{ eyebrowLabel || t('billing.review.payEyebrow') }}
    </div>

    <!-- Mode: showing the currently selected saved card -->
    <template v-if="viewMode === 'card' && activeCard">
      <SavedCardDisplay
        :card="activeCard"
        :show-default-pill="true"
      />
      <button
        v-if="hasAlternatives"
        type="button"
        class="payment-block__switch"
        :disabled="disabled"
        @click="setView('select')"
      >
        <i class="fa-regular fa-credit-card" />
        <span>{{ t('billing.review.payWithAnotherCard') }}</span>
      </button>
    </template>

    <!-- Mode: list of selectable saved cards + add-new option -->
    <template v-else-if="viewMode === 'select'">
      <div class="payment-block__select">
        <div class="payment-block__select-header">
          {{ t('billing.review.chooseACard') }}
        </div>
        <ul class="payment-block__select-list">
          <li
            v-for="card in selectableCards"
            :key="card.id"
            class="payment-block__select-item"
            :class="{ 'payment-block__select-item--active': card.id === activeCardId }"
            @click="onPickSaved(card)"
          >
            <div class="payment-block__select-brand">
              <i :class="brandIcon(card.brand)" />
            </div>
            <div class="payment-block__select-info">
              <div class="payment-block__select-line">
                <span>{{ card.brand }} ···· {{ card.last4 }}</span>
                <span
                  v-if="card.isDefault"
                  class="payment-block__select-pill"
                >
                  {{ t('billing.subscriptionPage.paymentMethods.default') }}
                </span>
              </div>
              <div class="payment-block__select-meta">
                {{ card.ownerName }} · {{ t('billing.subscriptionPage.paymentMethods.expires') }} {{ card.expiry }}
              </div>
            </div>
            <i
              v-if="card.id === activeCardId"
              class="fa-regular fa-check payment-block__select-check"
            />
          </li>
          <li
            class="payment-block__select-item payment-block__select-item--add"
            @click="setView('new')"
          >
            <div class="payment-block__select-brand payment-block__select-brand--add">
              <i class="fa-regular fa-plus" />
            </div>
            <div class="payment-block__select-info">
              <div class="payment-block__select-line">
                <span class="payment-block__select-add-label">
                  {{ t('billing.review.addNewCard') }}
                </span>
              </div>
              <div class="payment-block__select-meta">
                {{ t('billing.review.addNewCardHelp') }}
              </div>
            </div>
            <i class="fa-regular fa-chevron-right payment-block__select-chevron" />
          </li>
        </ul>
        <button
          type="button"
          class="payment-block__switch payment-block__switch--secondary"
          :disabled="disabled"
          @click="setView('card')"
        >
          <i class="fa-regular fa-arrow-left" />
          <span>{{ t('billing.review.cancelChoice') }}</span>
        </button>
      </div>
    </template>

    <!-- Mode: Adyen Drop-in for a brand-new card (and pay with it) -->
    <template v-else-if="viewMode === 'new'">
      <AdyenCardDropin
        :amount="amount"
        :currency="currency"
        :allowed-methods="allowedMethods"
        :submit-handler="submitHandler"
        :details-handler="detailsHandler"
        @success="$emit('success')"
        @error="onError"
      />
      <button
        v-if="hasDefaultCard"
        type="button"
        class="payment-block__switch payment-block__switch--secondary"
        :disabled="disabled"
        @click="setView(hasAlternatives ? 'select' : 'card')"
      >
        <i class="fa-regular fa-arrow-left" />
        <span>{{ t('billing.review.useSavedCard') }}</span>
      </button>
    </template>

    <Message
      v-if="errorMessage"
      severity="error"
      :closable="false"
      class="mt-2"
    >
      {{ errorMessage }}
    </Message>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Message from 'primevue/message';
import SavedCardDisplay from '@/components/billing/shared/SavedCardDisplay.vue';
import AdyenCardDropin from '@/components/billing/dialogs/AdyenCardDropin.vue';
import { brandIconFor } from '@/composables/billing/useBrandIcon';
import { ActionResult } from '@/composables/billing/billingApi';
import { PaymentMethodItem } from '@/types/billing-types';

export type PaymentMode = 'existing' | 'new';
type ViewMode = 'card' | 'select' | 'new';

/**
 * Payment block that handles three states:
 *  - `card`   : the currently selected saved card is shown (default-or-picked).
 *  - `select` : list of all valid saved cards + an "add new card" option.
 *  - `new`    : Adyen Drop-in for a brand-new card.
 *
 * The parent owns the actual payment handlers (`submitHandler` for the Drop-in
 * flow, `detailsHandler` for 3DS confirm). When the user pays with a saved
 * card, the parent reads `paymentMethodId` (v-model) and sends it to the
 * back; when they pay with a new one, the Drop-in fires `submitHandler`
 * directly without setting `paymentMethodId`.
 */
export default defineComponent({
  name: 'PaymentBlock',

  components: { Message, SavedCardDisplay, AdyenCardDropin },

  props: {
    /** Current default card from /overview (drives the initial "card" view). */
    defaultCard: {
      type: Object as PropType<PaymentMethodItem | null>,
      default: null,
    },

    /** Full list of saved cards (used to build the selection list). */
    allCards: {
      type: Array as PropType<PaymentMethodItem[]>,
      default: () => [],
    },

    /** Amount to show in the Adyen Drop-in (used by Adyen for the Pay button). */
    amount: {
      type: Number,
      required: true,
    },

    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },

    submitHandler: {
      type: Function as PropType<(stateData: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    detailsHandler: {
      type: Function as PropType<(stateData: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    /**
     * Adyen payment methods to expose in the Drop-in for the « new method »
     * flow. Forwarded as-is to {@link AdyenCardDropin.allowedMethods}.
     *   - Plan flows (ChangePlan / Migrate V5)  → ['scheme', 'paypal', 'sepadirectdebit']
     *   - One-shot flows (BuyCredits / Repay)   → ['scheme', 'paypal']
     *   - Add-a-card flow (AddPaymentMethod)    → ['scheme', 'paypal']
     */
    allowedMethods: {
      type: Array as PropType<string[]>,
      default: () => ['scheme'],
    },

    /** v-model:paymentMode — sync with parent (existing/new). */
    paymentMode: {
      type: String as PropType<PaymentMode>,
      default: 'existing',
    },

    /** v-model:paymentMethodId — id of the chosen saved card, or null = use default. */
    paymentMethodId: {
      type: String as PropType<string | null>,
      default: null,
    },

    errorMessage: {
      type: String as PropType<string | null>,
      default: null,
    },

    showEyebrow: {
      type: Boolean,
      default: true,
    },

    eyebrowLabel: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:paymentMode',
    'update:paymentMethodId',
    'success',
    'error',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();

    const hasDefaultCard = computed(() => props.defaultCard !== null);

    // Currently selected saved card id (null = use default).
    const activeCardId = ref<string | null>(
      props.paymentMethodId ?? props.defaultCard?.id ?? null,
    );

    // Resolved card object used by the "card" view header.
    const activeCard = computed<PaymentMethodItem | null>(() => {
      if (activeCardId.value) {
        const found = props.allCards.find((c) => c.id === activeCardId.value);
        if (found) return found;
      }
      return props.defaultCard;
    });

    // Valid, non-expired cards displayed in the picker. We let the user pick
    // even the current default — selecting it just confirms the existing
    // choice. Expired cards are hidden because they would refuse payment.
    const selectableCards = computed<PaymentMethodItem[]>(() => (
      props.allCards.filter((c) => !c.isExpired)
    ));

    const hasAlternatives = computed(() => (
      // Show the "Payer avec une autre carte" CTA only if there's at least
      // one card OTHER than the active one, OR if the user can add a new
      // one (always true). We prefer to always show it when there are 2+
      // saved cards or when an "add new" path is meaningful.
      selectableCards.value.length >= 1
    ));

    const viewMode = ref<ViewMode>(
      hasDefaultCard.value ? 'card' : 'new',
    );

    watch(
      () => props.paymentMode,
      (v) => {
        const target: ViewMode = v === 'new' && !hasDefaultCard.value ? 'new' : 'card';
        if (target !== viewMode.value && (v === 'existing' || !hasDefaultCard.value)) {
          viewMode.value = target;
        }
      },
    );

    // Auto-switch to `new` when no default card exists at all.
    watch(hasDefaultCard, (has) => {
      if (!has) {
        viewMode.value = 'new';
        emit('update:paymentMode', 'new');
      } else if (viewMode.value === 'new' && !props.paymentMethodId) {
        viewMode.value = 'card';
        emit('update:paymentMode', 'existing');
      }
    });

    const setView = (mode: ViewMode) => {
      viewMode.value = mode;
      emit('update:paymentMode', mode === 'new' ? 'new' : 'existing');
    };

    const onPickSaved = (card: PaymentMethodItem) => {
      activeCardId.value = card.id;
      // null when picking the default: keeps the back free to fall back to
      // `getDefaultOrOneToken`. Non-null otherwise so a specific token is used.
      emit(
        'update:paymentMethodId',
        card.id === props.defaultCard?.id ? null : card.id,
      );
      setView('card');
    };

    const onError = (err: string) => emit('error', err);

    const brandIcon = (brand: string): string => brandIconFor(brand);

    return {
      t,
      hasDefaultCard,
      hasAlternatives,
      activeCard,
      activeCardId,
      selectableCards,
      viewMode,
      setView,
      onPickSaved,
      onError,
      brandIcon,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.payment-block {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__eyebrow {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #94a3b8;
  }

  &__switch {
    appearance: none;
    background: transparent;
    border: none;
    padding: 4px 0;
    color: $brand-color-primary;
    font-size: 0.8125rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    align-self: flex-start;
    transition: color 140ms ease;

    &:hover:not(:disabled) {
      color: #5a8a06;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i {
      font-size: 0.75rem;
    }

    &--secondary {
      color: #64748b;

      &:hover:not(:disabled) {
        color: #0f172a;
      }
    }
  }

  // ─── Select view ────────────────────────────────────────────────────────
  &__select {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__select-header {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #94a3b8;
  }

  &__select-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__select-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;

    &:hover {
      border-color: $brand-color-primary;
      background: #fafffb;
    }

    &--active {
      border-color: $brand-color-primary;
      background: rgba(148, 200, 64, 0.06);
    }

    &--add {
      border-style: dashed;
      border-color: #cbd5e1;

      &:hover {
        border-color: $brand-color-primary;
        border-style: solid;
      }
    }
  }

  &__select-brand {
    width: 38px;
    height: 28px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #0f172a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    flex-shrink: 0;

    &--add {
      background: rgba(148, 200, 64, 0.12);
      color: $brand-color-primary;
      font-size: 0.9rem;
    }
  }

  &__select-info {
    flex: 1;
    min-width: 0;
  }

  &__select-line {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    color: #0f172a;
  }

  &__select-meta {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 2px;
  }

  &__select-pill {
    background: rgba(148, 200, 64, 0.16);
    color: #629d03;
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 2px 7px;
    border-radius: 100px;
  }

  &__select-add-label {
    color: $brand-color-primary;
  }

  &__select-check {
    color: $brand-color-primary;
    font-size: 0.9375rem;
    flex-shrink: 0;
  }

  &__select-chevron {
    color: #cbd5e1;
    font-size: 0.75rem;
    flex-shrink: 0;
  }
}
</style>
